import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  EditEnablePwdDialogue,
  type EditEnablePwdFormValues,
} from 'components/DeviceCredentials/EditEnablePwdDialogue';
import { NoCredentialsBox } from 'components/NoCredentialsBox/NoCredentialsBox';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import { useCallback, useState } from 'react';
import { generateGuid } from 'utils/guid';
import {
  type EnablePassword,
  EnablePasswordsList,
} from './EnablePasswordsList';

type Props = {
  privileges: EnablePassword[];
  onPrivilegesChange: (privileges: EnablePassword[]) => void;
};

export const PrivilegedCredentials = (props: Props) => {
  const confirmDeleteDialog = useSimpleConfirmDialog();
  const [editedCredential, setEditedCredential] = useState<
    EnablePassword | 'new' | null
  >(null);

  const getInitialValues = useCallback((): EditEnablePwdFormValues => {
    if (editedCredential === 'new') {
      return {
        username: '',
        password: '',
        retypePassword: '',
        expirationEnabled: false,
        expirationValue: null,
        includeNetworks: { ips: ['0.0.0.0/0', '::/0'] },
        excludeNetworks: { ips: [] },
        notes: '',
      };
    }

    if (editedCredential) {
      return {
        username: editedCredential.username ?? '',
        password: '',
        retypePassword: '',
        expirationEnabled: editedCredential.expirationDate?.enabled ?? false,
        expirationValue: editedCredential.expirationDate?.value
          ? new Date(editedCredential.expirationDate.value)
          : null,
        includeNetworks: { ips: editedCredential.includeNetworks ?? [] },
        excludeNetworks: { ips: editedCredential.excludeNetworks ?? [] },
        notes: editedCredential.notes ?? '',
      };
    }

    stumble(
      'warning: edit/add dialog was invoked with `null` editedCredential, that is impossible',
    );
    return {
      username: '',
      password: '',
      retypePassword: '',
      expirationEnabled: false,
      expirationValue: null,
      includeNetworks: { ips: [] },
      excludeNetworks: { ips: [] },
      notes: '',
    };
  }, [editedCredential]);

  const handleSaveCredential = useStableCallback(
    (values: EditEnablePwdFormValues) => {
      const credentialData = {
        username: values.username,
        password: values.password,
        includeNetworks: values.includeNetworks.ips,
        excludeNetworks: values.excludeNetworks.ips,
        notes: values.notes,
        expirationDate: {
          enabled: values.expirationEnabled,
          value:
            values.expirationEnabled && values.expirationValue
              ? values.expirationValue.toISOString()
              : undefined,
        },
      };

      if (editedCredential === 'new') {
        const maxPriority = Math.max(
          0,
          ...props.privileges.map((c) => c.priority),
        );

        const newCredential: EnablePassword = {
          ...credentialData,
          id: generateGuid(),
          priority: maxPriority + 1,
          // encrypt password for new credential
          custom: true,
        };

        props.onPrivilegesChange([...props.privileges, newCredential]);
      } else if (editedCredential) {
        props.onPrivilegesChange(
          props.privileges.map((existingCredential) => {
            if (existingCredential.id === editedCredential.id) {
              return {
                ...existingCredential,
                ...credentialData,
                // when password is an empty string in form values, keep the old value unchanged.
                password:
                  credentialData.password || existingCredential.password,
                // when the password is changed, mark the credential as custom to encrypt password
                ...(credentialData.password ? { custom: true } : {}),
                // user should not be able to change username in edit mode
                username: existingCredential.username ?? '',
              };
            }
            return existingCredential;
          }),
        );
      }

      setEditedCredential(null);
    },
  );

  const handleEditCredential = useCallback((credential: EnablePassword) => {
    return () => {
      setEditedCredential(credential);
    };
  }, []);

  const handleDeleteCredential = useStableCallback((id: string) => {
    return async () => {
      if (
        await confirmDeleteDialog.show(
          {
            header: 'Delete credential',
            subHeader: 'Are you sure you want to delete this credential?',
            btnConfirm: 'Delete',
            btnSecondaryConfirm: 'Cancel',
          },
          {
            btnConfirmDataTestId: 'privileges-dialog-remove-confirm',
          },
        )
      ) {
        props.onPrivilegesChange(props.privileges.filter((c) => c.id !== id));
      }
    };
  });

  const handleReorder = useStableCallback(
    (_data: EnablePassword[], draggedIndex: number, targetIndex: number) => {
      const result = [...props.privileges];
      const [removed] = result.splice(draggedIndex, 1);
      if (removed) {
        result.splice(targetIndex, 0, removed);
      }
      props.onPrivilegesChange(
        result.map((c, idx) => ({ ...c, priority: idx + 1 })),
      );
    },
  );

  return (
    <>
      <EnablePasswordsList
        className='-mx -mb -mt-4 rounded-b-lg bg-transparent flex-1'
        passwords={props.privileges}
        onAddCredentialClick={() => setEditedCredential('new')}
        onDropPassword={handleReorder}
        onEditPasswordClick={handleEditCredential}
        onDeletePasswordClick={handleDeleteCredential}
        noCredentialBox={
          <NoCredentialsBox
            title='No stored passwords'
            icon='fa-info-circle'
            type='info'
            className='-mx-2 -mt-2 border-t-0'
          />
        }
      />

      {editedCredential !== null && (
        <EditEnablePwdDialogue
          initialValues={getInitialValues()}
          onClose={() => setEditedCredential(null)}
          onSubmit={handleSaveCredential}
          formType={editedCredential === 'new' ? 'add' : 'edit'}
        />
      )}

      {confirmDeleteDialog.render()}
    </>
  );
};
